/**
 * @file index.tsx
 *
 * @fileoverview Renders the form widget to insert screening questions into a job form.
 */
import React, { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  getPlaceholder,
  getSetting,
} from '~/jobiqo/components/Form/helpers/formSettings';
import { EntityField } from '~/jobiqo/models/entity';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { FormItem } from '~/vcl/atoms/01-forms/11-form-item';
import { FormLabel } from '~/vcl/atoms/01-forms/11-form-item/01-form-label';
import { FormDescription } from '~/vcl/atoms/01-forms/11-form-item/02-form-description';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { CloseIcon } from '~/vcl/icons/Close';
import { Checkbox } from '~/vcl/atoms/01-forms/02-checkbox';
import { useIntl } from 'react-intl';
import { QuestionInput } from '~/jobiqo/types/types';
import { getCommonTranslations } from '~/jobiqo/utils/intl';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * Label to display in each item that is added.
   */
  singleItemLabel?: string;
  /**
   * If the form should have questions (check box is checked).
   */
  hasQuestions?: boolean;
}

export default function ScreeningQuestions({
  entityField,
  singleItemLabel,
  hasQuestions = false,
}: Props) {
  const intl = useIntl();
  const {
    control,
    setError,
    clearErrors,
    getValues,
    setValue,
  } = useFormContext();
  const { fields, remove, append } = useFieldArray({
    control,
    name: entityField.id,
  });
  const { ADD_ANOTHER, FIELD_REQUIRED } = getCommonTranslations();

  const addNew = () => {
    append({ question: '', required: false } as QuestionInput, {
      shouldFocus: false,
    });
  };

  function handleAddAnotherClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    addNew();
  }

  useEffect(() => {
    if (hasQuestions) {
      if (fields.length === 0 && entityField.defaultKeyValues.length > 0) {
        // Reset any existing questions.
        setValue(entityField.id, []);
        // Populate default questions.
        append(
          entityField.defaultKeyValues.map((item: any, key: any) => {
            return {
              question: item.text,
              required: item.required,
            } as QuestionInput;
          }),
          {
            shouldFocus: false,
          },
        );
      } else {
        if (fields.length === 0 && entityField.defaultKeyValues.length === 0) {
          // Reset any existing questions.
          setValue(entityField.id, []);
          // Add one empty question.
          addNew();
        }
      }
    }
  }, [entityField, fields, hasQuestions]);

  useEffect(() => {
    if (!hasQuestions) {
      // If no questions were added then we should
      // send an empty array.
      setValue(entityField.id, []);
    }
  }, [hasQuestions]);

  if (!hasQuestions) {
    return null;
  }

  return (
    <FormItem>
      <Spacing />
      <FormLabel required={entityField.required}>{entityField.label}</FormLabel>
      <FlexItems gap={3}>
        {fields.map((field, index) => {
          const fieldName = `${entityField.id}.${index}`;
          const questionFieldName = `${fieldName}.question`;
          const requiredFieldName = `${fieldName}.required`;
          const isFirst = index === 0;

          return (
            <FlexItems key={fieldName} flexWrap="nowrap">
              <FlexItem>
                <Controller
                  name={questionFieldName}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        id={field.name}
                        type={entityField.type}
                        required={entityField.required}
                        disabled={entityField.disabled}
                        error={!!error}
                        label={singleItemLabel}
                        size={60}
                        description={entityField.description}
                        placeholder={getPlaceholder(entityField)}
                        maxLength={getSetting('max_length', entityField)}
                      />
                      {isFirst && entityField.description && (
                        <FormDescription content={entityField.description} />
                      )}
                      {error && <InlineError message={error.message} />}
                    </>
                  )}
                />
                <Spacing />
                <Controller
                  name={requiredFieldName}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <Checkbox
                          {...field}
                          id={field.name}
                          checked={field.value}
                          disabled={entityField.disabled}
                          error={!!error}
                          label={intl.formatMessage({
                            id:
                              'job_application_quick_screening_question_required',
                            defaultMessage: 'This question is required.',
                          })}
                          onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            const values = getValues();
                            field.onChange(checked);

                            if (
                              checked &&
                              !values[entityField.id]?.[index]?.question
                            ) {
                              setError(questionFieldName, {
                                type: 'required',
                                message: FIELD_REQUIRED,
                              });
                            } else {
                              clearErrors(questionFieldName);
                            }
                          }}
                        />
                        {isFirst && entityField.description && (
                          <FormDescription content={entityField.description} />
                        )}
                        {!!error && <InlineError message={error.message} />}
                      </>
                    );
                  }}
                />
                <Spacing size="medium" />
              </FlexItem>
              {fields.length > 1 && (
                <FlexItem>
                  <Button
                    raised={false}
                    iconed={true}
                    buttonStyle="danger"
                    type="button"
                    onClick={() => remove(index)}
                  >
                    <CloseIcon title="remove" />
                  </Button>
                </FlexItem>
              )}
              <Spacing />
            </FlexItems>
          );
        })}
      </FlexItems>
      <Spacing />
      <Button buttonStyle="primary" onClick={handleAddAnotherClick}>
        {ADD_ANOTHER}
      </Button>
    </FormItem>
  );
}

export { ScreeningQuestions };
